import React, { useState } from 'react'
import { useAppStore, type RegisterData } from '@cya-routes/shared'
import { signInWithGoogle, signUpWithEmail } from '../../services/firebase/auth'

interface RegisterFormProps {
  onSuccess?: () => void
  onSwitchToLogin?: () => void
}

export function RegisterForm({ onSuccess, onSwitchToLogin }: RegisterFormProps) {
  const [formData, setFormData] = useState<RegisterData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    displayName: '',
    username: ''
  })
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  // const [isCheckingUsername, setIsCheckingUsername] = useState(false)
  
  const { register, auth } = useAppStore()
  
  const sanitizeDisplayName = (value: string) => value.replace(/\s{2,}/g, ' ')

  const generateUsernameFromDisplayName = (value: string) => {
    if (!value) return ''
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '')
      .slice(0, 20)
  }

  const handleInputChange = (field: keyof RegisterData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError('')
  }

  const handleFirstNameChange = (value: string) => {
    setFormData(prev => {
      const updated: RegisterData = { ...prev, firstName: value }
      if (!prev.displayName.trim()) {
        const sanitized = sanitizeDisplayName(value)
        const derivedUsername = generateUsernameFromDisplayName(sanitized)
        return {
          ...updated,
          displayName: sanitized,
          lastName: '',
          username: derivedUsername || updated.username
        }
      }
      return updated
    })
    setError('')
  }

  const handleDisplayNameChange = (value: string) => {
    setFormData(prev => {
      const previousGenerated = generateUsernameFromDisplayName(prev.displayName)
      const sanitized = sanitizeDisplayName(value)
      const trimmed = sanitized.trim()
      const parts = trimmed ? trimmed.split(' ') : []
      const derivedLastName = parts.slice(1).join(' ')
      const generatedUsername = generateUsernameFromDisplayName(trimmed)
      const shouldUpdateUsername = !prev.username || prev.username === previousGenerated

      return {
        ...prev,
        displayName: sanitized,
        lastName: derivedLastName,
        username: shouldUpdateUsername ? generatedUsername : prev.username
      }
    })
    setError('')
  }
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const trimmedFirstName = formData.firstName.trim()
    const normalizedDisplayName = sanitizeDisplayName(formData.displayName).trim()
    const derivedLastName = normalizedDisplayName.split(' ').slice(1).join(' ')
    const sanitizedUsername = formData.username.trim()
    const sanitizedEmail = formData.email.trim()
    
    // Validation
    if (!trimmedFirstName) {
      setError('First name is required')
      return
    }

    if (!/\S+\s+\S+/.test(normalizedDisplayName)) {
      setError('Display name must include at least two words. Try something like "Vince Bikeroutes".')
      return
    }

    if (!sanitizedEmail) {
      setError('Email is required')
      return
    }

    if (formData.password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }
    
    if (sanitizedUsername.length < 3) {
      setError('Username must be at least 3 characters long')
      return
    }
    
    try {
      // Set loading state
      const registrationPayload: RegisterData = {
        ...formData,
        email: sanitizedEmail,
        firstName: trimmedFirstName,
        displayName: normalizedDisplayName,
        lastName: derivedLastName,
        username: sanitizedUsername
      }

      await register(registrationPayload)
      
      // Sign up with Firebase with additional user data
      await signUpWithEmail(sanitizedEmail, formData.password, normalizedDisplayName, {
        firstName: trimmedFirstName,
        lastName: derivedLastName,
        username: sanitizedUsername,
        displayName: normalizedDisplayName
      })
      
      // The authInitializer will handle syncing the user to the store
      // and creating the user profile in Firestore
      
      onSuccess?.()
    } catch (err: any) {
      console.error('Registration error:', err)
      
      // Handle specific Firebase errors
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in instead.')
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.')
      } else if (err.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters.')
      } else {
        setError('Registration failed. Please try again.')
      }
    }
  }

  const handleGoogleSignIn = async () => {
    setError('')
    
    try {
      await signInWithGoogle()
      onSuccess?.()
    } catch (err) {
      setError('Google sign-up failed. Please try again.')
    }
  }
  
  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center">
            Create Account
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-center mt-2">
            Join the CYA Routes community
          </p>
        </div>
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}
        
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={auth.isLoading}
          className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed mb-6"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>
        
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300 dark:border-gray-600" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
              Or create account with email
            </span>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => handleFirstNameChange(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="John"
              />
            </div>
            
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Display Name
              </label>
              <input
                id="displayName"
                type="text"
                value={formData.displayName}
                onChange={(e) => handleDisplayNameChange(e.target.value)}
                required
                pattern=".+ .+"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="Vince Bikeroutes"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Use two words to help people recognise you. Examples: Vince Bikeroutes, Simon GiantHampton.
              </p>
            </div>
          </div>
          
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Username
            </label>
            <input
              id="username"
              type="text"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="johndoe"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Your profile will be available at /user/{formData.username}
            </p>
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="john@example.com"
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Enter your password"
            />
          </div>
          
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Confirm your password"
            />
          </div>
          
          <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-md">
            <p className="font-medium mb-1">By creating an account you will:</p>
            <ul className="space-y-1">
              <li>• Automatically join the "Bike Routes" community</li>
              <li>• Be able to create and publish routes</li>
              <li>• Follow and join other cycling clubs</li>
              <li>• Get a personal profile page at /user/{formData.username || 'username'}</li>
            </ul>
          </div>
          
          <button
            type="submit"
            disabled={auth.isLoading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {auth.isLoading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
        
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                Already have an account?
              </span>
            </div>
          </div>
          
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="mt-3 w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
          >
            Sign In
          </button>
        </div>
      </div>
    </div>
  )
}
